import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import citiesData from "data/cities.json";
import prefecturesData from "data/prefectures.json";
import workOptionsData from "data/workOptions.json";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { RootState, AppDispatch } from "app/store";
import { btnDelay } from "common/utils";
import i18n from "features/shared/i18n";
import { getAccountProfile } from "features/account/api";
import {
  ParentGender,
  Glasses,
  Hairstyle,
  BodyType,
  FaceShape,
} from "features/account/types";
import { ParentLanguageEditModal } from "./ParentLanguageEditModal";
import { ProfileEditModal, ProfileEditSection } from "./ProfileEditModal";

const getGenderLabel = (gender: ParentGender | null): string => {
  switch (gender) {
    case ParentGender.MALE:
      return i18n.t("MSG_PARENT_ONBOARDING_GENDER_MALE");
    case ParentGender.FEMALE:
      return i18n.t("MSG_PARENT_ONBOARDING_GENDER_FEMALE");
    default:
      return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  }
};

const getIncomeLabel = (income: string | null): string => {
  switch (income) {
    case "under_5m":
      return i18n.t("MSG_PARENT_ONBOARDING_INCOME_UNDER_5M");
    case "5m_10m":
      return i18n.t("MSG_PARENT_ONBOARDING_INCOME_5M_10M");
    case "over_10m":
      return i18n.t("MSG_PARENT_ONBOARDING_INCOME_OVER_10M");
    case "prefer_not_to_say":
      return i18n.t("MSG_PARENT_ONBOARDING_INCOME_PREFER_NOT_TO_SAY");
    default:
      return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  }
};

const getUserRoleLabel = (role: string | null): string => {
  if (!role) return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  switch (role) {
    case "mother":
      return i18n.t("MSG_HOUSEHOLD_MEMBER_MOTHER");
    case "father":
      return i18n.t("MSG_HOUSEHOLD_MEMBER_FATHER");
    case "grandmother":
      return i18n.t("MSG_HOUSEHOLD_MEMBER_GRANDMOTHER");
    case "grandfather":
      return i18n.t("MSG_HOUSEHOLD_MEMBER_GRANDFATHER");
    default:
      return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  }
};

const getHouseholdMembersLabel = (members: string[] | null): string => {
  if (!members || members.length === 0)
    return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  return members
    .map((m) => i18n.t(`MSG_HOUSEHOLD_MEMBER_${m.toUpperCase()}`))
    .join(", ");
};

const getGlassesLabel = (glasses: Glasses | null): string => {
  switch (glasses) {
    case Glasses.NONE:
      return i18n.t("MSG_GLASSES_NONE_BY_PARENT");
    case Glasses.GLASSES:
      return i18n.t("MSG_GLASSES_YES_BY_PARENT");
    default:
      return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  }
};

const getHairstyleLabel = (hairstyle: Hairstyle | null): string => {
  switch (hairstyle) {
    case Hairstyle.SHORT:
      return i18n.t("MSG_HAIRSTYLE_SHORT_BY_PARENT");
    case Hairstyle.MEDIUM:
      return i18n.t("MSG_HAIRSTYLE_MEDIUM_BY_PARENT");
    case Hairstyle.LONG:
      return i18n.t("MSG_HAIRSTYLE_LONG_BY_PARENT");
    default:
      return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  }
};

const getBodyTypeLabel = (bodyType: BodyType | null): string => {
  switch (bodyType) {
    case BodyType.THIN:
      return i18n.t("MSG_BODY_TYPE_THIN_BY_PARENT");
    case BodyType.SLIGHTLY_THIN:
      return i18n.t("MSG_BODY_TYPE_SLIGHTLY_THIN_BY_PARENT");
    case BodyType.MEDIUM:
      return i18n.t("MSG_BODY_TYPE_MEDIUM_BY_PARENT");
    case BodyType.SLIGHTLY_CHUBBY:
      return i18n.t("MSG_BODY_TYPE_SLIGHTLY_CHUBBY_BY_PARENT");
    case BodyType.CHUBBY:
      return i18n.t("MSG_BODY_TYPE_CHUBBY_BY_PARENT");
    default:
      return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  }
};

const getFaceShapeLabel = (faceShape: FaceShape | null): string => {
  switch (faceShape) {
    case FaceShape.ROUND:
      return i18n.t("MSG_FACE_SHAPE_ROUND_BY_PARENT");
    case FaceShape.OVAL:
      return i18n.t("MSG_FACE_SHAPE_OVAL_BY_PARENT");
    case FaceShape.OBLONG:
      return i18n.t("MSG_FACE_SHAPE_OBLONG_BY_PARENT");
    default:
      return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  }
};

const getLocaleLabel = (locale?: string | null): string => {
  switch (locale) {
    case "en":
      return i18n.t("MSG_PARENT_PAGE_LANGUAGE_EN");
    case "zh_tw":
    case "zh_cn":
      return i18n.t("MSG_PARENT_PAGE_LANGUAGE_ZH_TW");
    case "ja":
    case "ja_kanji":
    default:
      return i18n.t("MSG_PARENT_PAGE_LANGUAGE_JA");
  }
};

const getIndustryLabel = (industry: string | null): string => {
  if (!industry) return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  const opt = workOptionsData.industry.find((o) => o.key === industry);
  return opt ? i18n.t(opt.labelKey) : industry;
};

const getOccupationLabel = (occupation: string | null): string => {
  if (!occupation) return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  const opt = workOptionsData.occupation.find((o) => o.key === occupation);
  return opt ? i18n.t(opt.labelKey) : occupation;
};

const getJobTitleLabel = (jobTitle: string | null): string => {
  if (!jobTitle) return i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");
  const opt = workOptionsData.jobTitle.find((o) => o.key === jobTitle);
  return opt ? i18n.t(opt.labelKey) : jobTitle;
};

export const ProfileSection = () => {
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const parent = useSelector((state: RootState) => state.user.parent);
  const parentLocale = useSelector(
    (state: RootState) => state.config.parentConfig.locale,
  );
  const parentProfile = useSelector(
    (state: RootState) => state.account.parentProfile,
  );
  const [editSection, setEditSection] = useState<ProfileEditSection | null>(
    null,
  );
  const [languageModalOpen, setLanguageModalOpen] = useState(false);

  // 都道府県名を取得
  const prefectureName = useMemo(() => {
    if (!parentProfile?.region) return null;
    const pref = prefecturesData.prefectures.find(
      (p) => p.code === parentProfile.region,
    );
    return pref?.name || null;
  }, [parentProfile?.region]);

  // 市区町村名を取得
  const cityName = useMemo(() => {
    if (!parentProfile?.region || !parentProfile?.city) return null;
    const cities =
      (citiesData as Record<string, { code: string; name: string }[]>)[
        parentProfile.region
      ] || [];
    const city = cities.find((c) => c.code === parentProfile.city);
    return city?.name || null;
  }, [parentProfile?.region, parentProfile?.city]);

  useEffect(() => {
    if (parent?.uid) {
      handleDialog({ type: DialogType.LOADING, value: true });
      dispatch(getAccountProfile({ uid: parent.uid }))
        .unwrap()
        .finally(() =>
          handleDialog({ type: DialogType.LOADING, value: false }),
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent?.uid]);

  const handleOpenEditModal = (section: ProfileEditSection) => {
    btnDelay(() => setEditSection(section));
  };

  const handleOpenLanguageModal = () => {
    btnDelay(() => setLanguageModalOpen(true));
  };

  const notSet = i18n.t("MSG_PARENT_PAGE_PROFILE_NOT_SET");

  return (
    <>
      <div className="bg-white rounded-[16px] p-6">
        <div className="flex-row-el items-center gap-2 text-[27px] text-textcolor-black mb-5 pb-3 border-b border-outline">
          <span className="material-symbols-outlined !text-[45px] text-red-100">
            person
          </span>
          {i18n.t("MSG_PARENT_PAGE_PROFILE_SECTION_TITLE")}
        </div>

        <div className="mb-4">
          <div className="flex-row-el items-center justify-between mb-2">
            <div className="text-[21px] text-gray2-100">
              {i18n.t("MSG_PARENT_PAGE_DISPLAY_LANGUAGE")}
            </div>
            <button
              onClick={handleOpenLanguageModal}
              className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1 px-3 text-[18px] cursor-pointer hover:bg-gray2-20 transition-all"
            >
              {i18n.t("MSG_PARENT_PAGE_CHANGE")}
            </button>
          </div>
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_PARENT_PAGE_DISPLAY_LANGUAGE")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getLocaleLabel(parentLocale)}
            </div>
          </div>
        </div>

        {/* 基本情報 */}
        <div className="mb-4">
          <div className="flex-row-el items-center justify-between mb-2">
            <div className="text-[21px] text-gray2-100">
              {i18n.t("MSG_PARENT_PAGE_PROFILE_BASIC_INFO")}
            </div>
            <button
              onClick={() => handleOpenEditModal("basic")}
              className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1 px-3 text-[18px] cursor-pointer hover:bg-gray2-20 transition-all"
            >
              {i18n.t("MSG_PARENT_PAGE_CHANGE")}
            </button>
          </div>

          {/* 性別 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_PROFILE_SUBSTEP_GENDER_BY_PARENT")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getGenderLabel(parentProfile?.gender ?? null)}
            </div>
          </div>

          {/* 生年月日 */}
          <div className="flex-row-el items-center py-3">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_PROFILE_SUBSTEP_BIRTHDAY_BY_PARENT")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {parentProfile?.birthday
                ? dayjs(parentProfile.birthday).format("YYYY-MM-DD")
                : notSet}
            </div>
          </div>
        </div>

        {/* 見た目 */}
        <div className="mb-4">
          <div className="flex-row-el items-center justify-between mb-2">
            <div className="text-[21px] text-gray2-100">
              {i18n.t("MSG_PARENT_PROFILE_APPEARANCE")}
            </div>
            <button
              onClick={() => handleOpenEditModal("appearance")}
              className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1 px-3 text-[18px] cursor-pointer hover:bg-gray2-20 transition-all"
            >
              {i18n.t("MSG_PARENT_PAGE_CHANGE")}
            </button>
          </div>

          {/* メガネ */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_GLASSES_TITLE_BY_PARENT")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getGlassesLabel(parentProfile?.glasses ?? null)}
            </div>
          </div>

          {/* 髪型 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_HAIRSTYLE_TITLE_BY_PARENT")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getHairstyleLabel(parentProfile?.hairstyle ?? null)}
            </div>
          </div>

          {/* 体型 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_BODY_TYPE_TITLE_BY_PARENT")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getBodyTypeLabel(parentProfile?.body_type ?? null)}
            </div>
          </div>

          {/* 顔型 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_FACE_SHAPE_TITLE_BY_PARENT")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getFaceShapeLabel(parentProfile?.face_shape ?? null)}
            </div>
          </div>

          {/* 自己紹介 */}
          <div className="flex-row-el items-center py-3">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_PARENT_PAGE_PROFILE_NOTES")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {parentProfile?.self_introduction || notSet}
            </div>
          </div>
        </div>

        {/* 住まいの地域 */}
        <div className="mb-4">
          <div className="flex-row-el items-center justify-between mb-2">
            <div className="text-[21px] text-gray2-100">
              {i18n.t("MSG_PARENT_PROFILE_REGION")}
            </div>
            <button
              onClick={() => handleOpenEditModal("region")}
              className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1 px-3 text-[18px] cursor-pointer hover:bg-gray2-20 transition-all"
            >
              {i18n.t("MSG_PARENT_PAGE_CHANGE")}
            </button>
          </div>

          {/* 都道府県 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_PREFECTURE_TITLE")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {prefectureName || notSet}
            </div>
          </div>

          {/* 地域 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_CITY_TITLE")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {cityName || notSet}
            </div>
          </div>
        </div>

        {/* 職業情報 */}
        <div className="mb-4">
          <div className="flex-row-el items-center justify-between mb-2">
            <div className="text-[21px] text-gray2-100">
              {i18n.t("MSG_PARENT_PROFILE_WORK")}
            </div>
            <button
              onClick={() => handleOpenEditModal("work")}
              className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1 px-3 text-[18px] cursor-pointer hover:bg-gray2-20 transition-all"
            >
              {i18n.t("MSG_PARENT_PAGE_CHANGE")}
            </button>
          </div>

          {/* 職種 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_OCCUPATION_TITLE")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getOccupationLabel(parentProfile?.occupation ?? null)}
            </div>
          </div>

          {/* 役職 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_JOB_TITLE_LABEL")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getJobTitleLabel(parentProfile?.job_title ?? null)}
            </div>
          </div>

          {/* 業種 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_INDUSTRY_TITLE")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getIndustryLabel(parentProfile?.industry ?? null)}
            </div>
          </div>

          {/* 年収 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_PARENT_ONBOARDING_INCOME_TITLE")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getIncomeLabel(parentProfile?.annual_income ?? null)}
            </div>
          </div>
        </div>

        {/* 家族情報 */}
        <div>
          <div className="flex-row-el items-center justify-between mb-2">
            <div className="text-[21px] text-gray2-100">
              {i18n.t("MSG_PARENT_PROFILE_FAMILY")}
            </div>
            <button
              onClick={() => handleOpenEditModal("family")}
              className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1 px-3 text-[18px] cursor-pointer hover:bg-gray2-20 transition-all"
            >
              {i18n.t("MSG_PARENT_PAGE_CHANGE")}
            </button>
          </div>

          {/* 自分の役割 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_USER_ROLE_TITLE")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getUserRoleLabel(parentProfile?.user_role ?? null)}
            </div>
          </div>

          {/* 同居人 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_HOUSEHOLD_MEMBERS_TITLE")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {getHouseholdMembersLabel(
                parentProfile?.household_members ?? null,
              )}
            </div>
          </div>

          {/* 男の子の数 */}
          <div className="flex-row-el items-center py-3 border-b border-outline">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_BOYS_COUNT")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {parentProfile?.boys_count !== null &&
              parentProfile?.boys_count !== undefined
                ? parentProfile.boys_count
                : notSet}
            </div>
          </div>

          {/* 女の子の数 */}
          <div className="flex-row-el items-center py-3">
            <div className="w-[200px] text-[24px] text-textcolor-black">
              {i18n.t("MSG_GIRLS_COUNT")}
            </div>
            <div className="flex-1 text-[24px] text-textcolor-black">
              {parentProfile?.girls_count !== null &&
              parentProfile?.girls_count !== undefined
                ? parentProfile.girls_count
                : notSet}
            </div>
          </div>
        </div>
      </div>

      {editSection && (
        <ProfileEditModal
          open={!!editSection}
          section={editSection}
          onClose={() => setEditSection(null)}
        />
      )}
      {parent?.uid && (
        <ParentLanguageEditModal
          open={languageModalOpen}
          currentLocale={parentLocale}
          parentUid={parent.uid}
          onClose={() => setLanguageModalOpen(false)}
        />
      )}
    </>
  );
};
